import Bouncer from 'formbouncerjs';
import { selectFromDocument } from '3-components/Utils/DOMUtils';
import { ValidateEmail } from '3-components/Utils/StringUtils';
import { ValidateAusPhone } from '3-components/Utils/NumberUtils';
import { CallSubmission } from './LandingPageMultiStepModalTypes';
import DefaultModalContent from './DefaultModalContent';
import hydrateModalContent from './HydrateModalContent';

// for local / dev, Change this to the dev API deployment. Leave empty for inte / prod.
const API_ROUTE = '';
// Set True for local, non-API testing.
const IS_FLOW_TESTING = false;

const hideStep = (step: HTMLElement) => {
    step.style.visibility = 'hidden';
    step.style.opacity = '0';
    step.style.height = '0';
    step.style.position = 'absolute';
};

const showStep = (step: HTMLElement) => {
    step.style.visibility = 'visible';
    step.style.opacity = '1';
    step.style.height = 'auto';
    step.style.position = 'static';
};

const toggleSteps = (stepToHide: HTMLElement, stepToShow: HTMLElement) => {
    if (hideStep && showStep) {
        hideStep(stepToHide);
        showStep(stepToShow);
    }
};

const showFinalFormStep = (currentStep: HTMLElement, isError: boolean) => {
    const finalStep = selectFromDocument('.landing-page-multistep-modal-content.form-end');
    const successMessage = finalStep.querySelector('.notification-success');
    const failureMessage = finalStep.querySelector('.notification-error');

    hideStep(currentStep);
    showStep(finalStep);

    if (isError) {
        failureMessage?.classList.toggle('hidden');
    } else {
        successMessage?.classList.toggle('hidden');
    }
};

const submitFormData = async (request: Record<string, unknown> | CallSubmission, endpoint: string): Promise<Response> => {
    if (IS_FLOW_TESTING) {
        return new Promise(resolve => {
            setTimeout(() => {
                const response = new Response(JSON.stringify({ id: 0 }), { status: 200 });
                return resolve(response);
            }, 2000);
        });
    }
    const response = await fetch(`${API_ROUTE}/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request)
    });

    return response;
};

const handleCallSubmission = async (data: CallSubmission) => {
    const stepFour = selectFromDocument('.landing-page-multistep-modal-content.step-four');
    const form = selectFromDocument('form.call-me-form') as HTMLFormElement;
    const formData = new FormData(form);
    const submitBtn = form?.querySelector<HTMLButtonElement>('.js-submit');
    const loaderBtn = stepFour.querySelector('.landing-page-multistep-modal-loader-btn');

    if (!formData.get('phone') || !formData.get('details') || !ValidateAusPhone(formData.get('phone') as string)) {
        return;
    }

    loaderBtn?.classList.toggle('hidden');
    submitBtn?.classList.toggle('hidden');

    try {
        const requestData = {
            phoneNumber: formData.get('phone'),
            details: formData.get('details'),
            ...data
        };
        const response = await submitFormData(requestData, 'landingpageactions/download');
        if (response && response.ok) {
            showFinalFormStep(stepFour, false);
        } else {
            throw new Error('Error in Form submission');
        }
    } catch (error) {
        // show the final step with error message
        showFinalFormStep(stepFour, true);
    }
};

const handleBrochureSubmission = async () => {
    const stepThree = selectFromDocument('.landing-page-multistep-modal-content.step-three') as HTMLDivElement;
    const stepTwo = selectFromDocument('.landing-page-multistep-modal-content.step-two') as HTMLDivElement;
    const stepThreeBtn = stepThree?.querySelector<HTMLElement>('.landing-page-multistep-modal-btn');
    const stepFour = selectFromDocument('.landing-page-multistep-modal-content.step-four');
    const stepFourBtn = stepFour?.querySelector<HTMLElement>('.landing-page-multistep-modal-btn');
    const form = selectFromDocument('form.brochure-request-form') as HTMLFormElement;
    const submitBtn = form?.querySelector<HTMLButtonElement>('.js-submit');
    const loaderBtn = stepTwo.querySelector('.landing-page-multistep-modal-loader-btn');
    const formData = new FormData(form);

    try {
        const invalidPostcode = !formData.get('postcode') || (formData.get('postcode') as string).length > 4;
        const invalidName = !formData.get('name');
        const invalidEmail = !formData.get('emailAddress') || !ValidateEmail(formData.get('emailAddress') as string);

        if (invalidPostcode || invalidName || invalidEmail) {
            return;
        }

        loaderBtn?.classList.toggle('hidden');
        submitBtn?.classList.toggle('hidden');

        let requestData = {
            postcode: formData.get('postcode') as string,
            name: formData.get('name') as string,
            emailAddress: formData.get('emailAddress') as string,
            id: undefined
        } as CallSubmission;

        const response = await submitFormData(requestData, 'landingpageactions/download');

        if (response && response.ok) {
            const data = await response.json();
            const { id } = data;
            requestData = {
                ...requestData,
                id: Number(id)
            };
            toggleSteps(stepTwo, stepThree);
            stepThreeBtn?.addEventListener('click', () => {
                toggleSteps(stepThree, stepFour);
                stepFourBtn?.addEventListener('click', e => {
                    e.preventDefault();
                    handleCallSubmission(requestData);
                });
            });
        } else {
            throw new Error('Error in Form submission');
        }
    } catch (error) {
        // show the final step with error message
        showFinalFormStep(stepTwo, true);
    }
};

const startBrochureSubmission = async () => {
    const landingPageMultistepModalContainer = selectFromDocument('.landing-page-multistep-modal-cta');
    const multistepModalOverlay = selectFromDocument('.landing-page-multistep-modal-overlay');
    const stepOne = selectFromDocument('.landing-page-multistep-modal-content.step-one') as HTMLDivElement;
    const stepTwo = selectFromDocument('.landing-page-multistep-modal-content.step-two') as HTMLDivElement;
    const form = selectFromDocument('form.brochure-request-form') as HTMLFormElement;
    const submitBtn = form?.querySelector<HTMLButtonElement>('.js-submit');

    multistepModalOverlay?.classList.add('show');
    landingPageMultistepModalContainer.classList.add('open');
    toggleSteps(stepOne, stepTwo);

    submitBtn?.addEventListener('click', async e => {
        e.preventDefault();
        handleBrochureSubmission();
    });
};

// Run the functions on document ready
document.addEventListener('DOMContentLoaded', async () => {
    const body = selectFromDocument('body');
    const landingPageMultistepModalContainer = selectFromDocument('.landing-page-multistep-modal-cta');
    const multistepModalOverlay = selectFromDocument('.landing-page-multistep-modal-overlay');
    const stepOne = selectFromDocument('.landing-page-multistep-modal-content.step-one') as HTMLDivElement;
    const stepOneBtn = stepOne?.querySelector<HTMLElement>('.landing-page-multistep-modal-btn');
    const closeBtns = document.querySelectorAll<HTMLElement>('.js-modal-close');

    let modalConfig;

    if (IS_FLOW_TESTING) {
        modalConfig = DefaultModalContent;
    }

    const getModalConfig = await fetch(`${API_ROUTE}/landingpageactions/config`, { method: 'GET' });

    if (getModalConfig.ok) {
        modalConfig = await getModalConfig.json();
    }

    if (landingPageMultistepModalContainer?.classList.contains('show-on-scroll') && body) {
        window.addEventListener('scroll', () => {
            const currentScrollY = document.documentElement.scrollTop;
            if (currentScrollY >= (body.scrollHeight / 2)) {
                landingPageMultistepModalContainer.style.visibility = 'visible';
                landingPageMultistepModalContainer.style.opacity = '1';
            }
        });
    }

    if (landingPageMultistepModalContainer?.classList.contains('show-with-delay')) {
        setTimeout(() => {
            landingPageMultistepModalContainer.style.visibility = 'visible';
            landingPageMultistepModalContainer.style.opacity = '1';
        }, 500);
    }

    if (modalConfig) {
        hydrateModalContent(modalConfig);
    }

    Bouncer('form.brochure-request-form');
    Bouncer('form.call-me-form');

    stepOneBtn?.addEventListener('click', () => {
        startBrochureSubmission();
    });

    Array.from(closeBtns).forEach(btn => {
        btn.addEventListener('click', () => {
            multistepModalOverlay?.classList?.remove('show');
            landingPageMultistepModalContainer.classList.remove('open');
            if (landingPageMultistepModalContainer) {
                landingPageMultistepModalContainer.style.display = 'none';
            }
        });
    });
});
